import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '../Spinner';
import { ErrorMessage } from '../ErrorMessage';
import { StatCard } from '../ui/StatCard';
import { KingdomBrowseControls } from './KingdomBrowseControls';
import { KingdomRosterTable } from './KingdomRosterTable';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { COLORS } from '../../constants/theme';
import type { KingdomViewerShellProps } from './types';

/**
 * SF-169 Component 4: near-identical to Kingdom.tsx's current desktop layout (banner, StatCard
 * row, member table), parameterized to accept any Kingdom, plus KingdomBrowseControls in the
 * header. Roster renders via the shared KingdomRosterTable — the same component Kingdom.tsx uses.
 *
 * SF-151: shared with Kingdom.tsx ("My Kingdom") as well as KingdomViewer.tsx ("World Viewer") —
 * `browse` is omitted by Kingdom.tsx to keep it own-kingdom-only, `pageTitle`/`emptyMessage` let
 * each route keep its own copy.
 */
export function DesktopKingdomViewerShell({
  kingdom,
  isLoading,
  error,
  viewerProvinceId,
  pageTitle,
  emptyMessage,
  refresh,
  browse,
}: KingdomViewerShellProps) {
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const avgNetworth = kingdom && kingdom.stats.totalProvinces > 0
    ? Math.round(kingdom.stats.totalNetworth / kingdom.stats.totalProvinces)
    : 0;
  const avgLand = kingdom && kingdom.stats.totalProvinces > 0
    ? Math.round(kingdom.stats.totalLand / kingdom.stats.totalProvinces)
    : 0;

  return (
    <div className="max-w-6xl mx-auto px-4 pt-0.5 pb-4" style={{ backgroundColor: COLORS.bg }}>
      <h1 ref={h1Ref} tabIndex={-1} className="sr-only outline-none">{pageTitle}</h1>

      {browse && (
        <div
          data-testid="kingdom-browse-bar"
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${COLORS.border}`,
            background: COLORS.card,
          }}
        >
          <KingdomBrowseControls
            onSelect={browse.onSelect}
            onPrevious={browse.onPrevious}
            onNext={browse.onNext}
            onRandom={browse.onRandom}
            previousDisabled={browse.previousDisabled}
            nextDisabled={browse.nextDisabled}
          />
        </div>
      )}

      {isLoading && !kingdom && (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      )}

      {error && (
        <div className="py-4">
          <ErrorMessage message={error} />
          <div className="mt-4 text-center">
            <button
              onClick={() => refresh()}
              className="px-4 py-2 rounded"
              style={{ backgroundColor: COLORS.btnPrimary, color: COLORS.text.primary, minHeight: 44 }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {!isLoading && !error && !kingdom && (
        <div className="py-16 text-center">
          <p style={{ color: COLORS.text.tertiary }}>{emptyMessage}</p>
          <div className="mt-4">
            <button
              onClick={() => refresh()}
              className="px-4 py-2 rounded"
              style={{ backgroundColor: COLORS.btnPrimary, color: COLORS.text.primary, minHeight: 44 }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {kingdom && (
        <>
          <div className="mb-1 flex flex-col items-center mt-2">
            <h2 className="text-lg font-bold mb-1" style={{ color: COLORS.text.primary }}>
              The Kingdom of {kingdom.monarchProvinceName || 'Unnamed Kingdom'} ({kingdom.kingdomId})
            </h2>
            <div className="flex gap-0.5 mb-1" style={{ whiteSpace: 'nowrap' }}>
              <div className="w-28">
                <StatCard label="Provinces" value={`${kingdom.stats.totalProvinces}`} borderless />
              </div>
              <div className="w-32">
                <StatCard
                  label="Total Networth"
                  value={`${formatNumber(kingdom.stats.totalNetworth)}gc`}
                  sub={kingdom.networthRank ? `Rank #${kingdom.networthRank}` : `avg: ${formatNumber(avgNetworth)}gc`}
                  borderless
                />
              </div>
              <div className="w-28">
                <StatCard
                  label="Total Land"
                  value={`${formatNumber(kingdom.stats.totalLand)}`}
                  sub={kingdom.landRank ? `Rank #${kingdom.landRank}` : `avg: ${formatNumber(avgLand)} acres`}
                  borderless
                />
              </div>
              <div className="w-28">
                <StatCard label="Stance" value={formatEnumLabel(kingdom.stance)} borderless />
              </div>
              <div className="w-28">
                <StatCard label="Honour" value={kingdom.honourRank ? `#${kingdom.honourRank}` : '-'} borderless />
              </div>
              <div className="w-28">
                {/* SF-169 Component 4: RelationshipSummary only carries aggregate counts, not
                    per-kingdom names — a count badge, not DossierHeader's named clickable chips. */}
                <StatCard label="Wars" value={`⚔ ${kingdom.relationships.atWar}`} sub="At war" borderless />
              </div>
            </div>
            <Link
              to={`/world/dossier/${kingdom.kingdomId}`}
              style={{ color: COLORS.link, fontSize: 13, textDecoration: 'underline' }}
            >
              View Dossier
            </Link>
          </div>

          {kingdom.members.length === 0 ? (
            <div style={{ padding: 32, color: COLORS.text.muted, textAlign: 'center' }}>
              No provinces in this kingdom
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              <KingdomRosterTable
                members={kingdom.members}
                viewerProvinceId={viewerProvinceId}
                currentTick={kingdom.currentTick}
                kingdomId={kingdom.kingdomId}
                kingdomName={kingdom.monarchProvinceName || 'Unnamed Kingdom'}
                kingdomNw={kingdom.stats.totalNetworth}
                kingdomLand={kingdom.stats.totalLand}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
